import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Terminal, Loader2, AlertCircle, Send } from 'lucide-react'
import { routerOsWebSocketService } from '../../services/routerosWebSocketService'
import api from '../../services/api'
import { usePopupWindowDocumentChrome, ROUTER_TERMINAL_FAVICON_HREF } from '../../utils/popupWindowDocumentChrome'
import { useTerminalAI, DEFAULT_CONTEXT_LINES, DEFAULT_CONTEXT_LINES_PRO } from '../../hooks/useTerminalAI'
import TerminalAIButton from '../../components/TerminalAI/TerminalAIButton'
import TerminalAIPanel from '../../components/TerminalAI/TerminalAIPanel'

function routerApiUsername(router) {
  return router?.apiUsername || 'admin'
}

function routerApiPasswordForConnection(router) {
  return router?.apiPassword || router?.apiPasswordTemporaria || ''
}

function getRouterIp(router) {
  return router?.vpnTunnelIp || null
}

function useFormatters() {
  const { t } = useTranslation()
  const formatAsTable = useCallback((items) => {
    const keys = new Set()
    items.forEach((item) =>
      Object.keys(item).forEach((k) => {
        if (k !== '.id' && !k.startsWith('=')) keys.add(k)
      })
    )
    const cols = Array.from(keys)
    if (cols.length === 0) return t('routers.terminal.noData')

    const widths = {}
    cols.forEach((c) => { widths[c] = c.length })
    items.forEach((item) => {
      cols.forEach((c) => {
        const len = String(item[c] ?? '').length
        if (len > widths[c]) widths[c] = Math.min(len, 40)
      })
    })

    const header = cols.map((c) => c.padEnd(widths[c])).join('  ')
    const sep = cols.map((c) => '-'.repeat(widths[c])).join('  ')
    const rows = items.map((item) =>
      cols.map((c) => {
        const v = String(item[c] ?? '')
        return v.length > widths[c]
          ? v.substring(0, widths[c] - 1) + '…'
          : v.padEnd(widths[c])
      }).join('  ')
    )
    return [header, sep, ...rows].join('\n')
  }, [t])

  const formatAsKeyValue = useCallback((obj) => {
    const entries = Object.entries(obj).filter(
      ([k]) => !k.startsWith('.') && !k.startsWith('=') && k !== 'id' && k !== 'success' && k !== 'action'
    )
    if (entries.length === 0) return ''
    const maxKey = Math.max(...entries.map(([k]) => k.length))
    return entries
      .map(([k, v]) => {
        const val = v != null && typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')
        return `  ${k.padEnd(maxKey)} : ${val}`
      })
      .join('\n')
  }, [])

  const formatTerminalResult = useCallback((data) => {
    if (data == null) return ''
    if (typeof data === 'string') return data
    if (typeof data === 'number' || typeof data === 'boolean') return String(data)

    if (Array.isArray(data)) {
      if (data.length === 0) return t('routers.terminal.noResults')
      if (typeof data[0] === 'object' && data[0] !== null) return formatAsTable(data)
      return data.map(String).join('\n')
    }

    if (typeof data === 'object') {
      const items = data.data ?? data.result
      if (Array.isArray(items)) return formatTerminalResult(items)
      if (items != null && typeof items === 'object') return formatAsKeyValue(items)
      if (typeof items === 'string') return items
      if (data.message) return data.message
      if (data.success !== undefined && items == null) {
        return data.success ? t('routers.terminal.commandOk') : (data.error || t('common.status.error'))
      }
      return formatAsKeyValue(data)
    }

    return String(data)
  }, [t, formatAsTable, formatAsKeyValue])

  return { formatTerminalResult }
}

export default function RouterTerminal() {
  const { t } = useTranslation()
  const { routerId } = useParams()
  const [router, setRouter] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState(null)
  const [terminalCommand, setTerminalCommand] = useState('')
  const [terminalHistory, setTerminalHistory] = useState([])
  const [executingCommand, setExecutingCommand] = useState(false)
  const wsConnectedRef = useRef(false)
  const historyEndRef = useRef(null)
  const terminalHistoryRef = useRef([])
  terminalHistoryRef.current = terminalHistory
  const contextEntriesRef = useRef(DEFAULT_CONTEXT_LINES)
  const { formatTerminalResult } = useFormatters()

  const getTerminalBuffer = useCallback(() => {
    return terminalHistoryRef.current
      .slice(-contextEntriesRef.current)
      .map((e) =>
        e.type === 'command' ? `> ${e.content}` : e.content
      )
      .join('\n')
  }, [])

  const executeRouterCommand = useCallback(
    async (command) => {
      if (!router) return t('routers.terminal.routerNotLoaded')
      const routerIp = connectionStatus?.routerIp || getRouterIp(router)
      if (!routerIp) return t('routers.terminal.ipNotFound')

      const username = routerApiUsername(router)
      const password = routerApiPasswordForConnection(router)

      setTerminalHistory((prev) => [
        ...prev,
        { type: 'command', content: command, source: 'ai' },
      ])

      try {
        const result = await routerOsWebSocketService.executeCommand(
          routerId, routerIp, username, password, command
        )
        const formatted = formatTerminalResult(result.data || result)
        setTerminalHistory((prev) => [
          ...prev,
          { type: 'result', content: formatted },
        ])
        return formatted
      } catch (e) {
        const errMsg = e.message || t('routers.terminal.executeError')
        setTerminalHistory((prev) => [
          ...prev,
          { type: 'error', content: errMsg },
        ])
        return t('routers.terminal.errorPrefix', { msg: errMsg })
      }
    },
    [router, routerId, connectionStatus, formatTerminalResult, t]
  )

  const ai = useTerminalAI({
    systemProfileSlug: 'MikrotikRouterOS',
    deviceId: routerId,
    deviceName: router?.name,
    getTerminalBuffer,
    executeCommand: executeRouterCommand,
  })
  contextEntriesRef.current = ai.settings.proMode ? DEFAULT_CONTEXT_LINES_PRO : DEFAULT_CONTEXT_LINES

  usePopupWindowDocumentChrome({
    title: router?.name ? `${router.name} · Terminal · Automais` : t('routers.terminal.popupTitle'),
    faviconHref: ROUTER_TERMINAL_FAVICON_HREF,
  })

  const loadRouter = useCallback(async () => {
    try {
      setLoading(true)
      const { data: routerData } = await api.get(`/managed-devices/${routerId}`)
      setRouter(routerData)

      if (!routerOsWebSocketService.isConnected() || routerOsWebSocketService.getCurrentRouterId() !== routerId) {
        if (routerOsWebSocketService.isConnected()) await routerOsWebSocketService.disconnect()
        await routerOsWebSocketService.connect(routerId)
        wsConnectedRef.current = true
        await new Promise((r) => setTimeout(r, 800))
      }

      const routerIp = getRouterIp(routerData)
      const status = await routerOsWebSocketService.getStatus(routerId, routerIp)
      const resolvedIp = status?.router_ip || routerIp

      setConnectionStatus({
        connected: status?.connected ?? false,
        identity: status?.identity,
        routerIp: resolvedIp,
        error: null,
      })
    } catch (e) {
      setError(e.message || t('routers.terminal.loadError'))
    } finally {
      setLoading(false)
    }
  }, [routerId, t])

  useEffect(() => {
    loadRouter()
    return () => {
      if (wsConnectedRef.current) {
        routerOsWebSocketService.disconnect()
        wsConnectedRef.current = false
      }
    }
  }, [loadRouter])

  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [terminalHistory])

  const executeTerminalCommand = async () => {
    if (!terminalCommand.trim() || executingCommand || !router) return
    try {
      setExecutingCommand(true)
      const routerIp = connectionStatus?.routerIp || getRouterIp(router)
      if (!routerIp) throw new Error(t('routers.terminal.ipNotFoundError'))

      const username = routerApiUsername(router)
      const password = routerApiPasswordForConnection(router)

      const result = await routerOsWebSocketService.executeCommand(routerId, routerIp, username, password, terminalCommand)
      const formatted = formatTerminalResult(result.data || result)

      setTerminalHistory((prev) => [
        ...prev,
        { type: 'command', content: terminalCommand },
        { type: 'result', content: formatted },
      ])
      setTerminalCommand('')
    } catch (e) {
      setTerminalHistory((prev) => [
        ...prev,
        { type: 'command', content: terminalCommand },
        { type: 'error', content: e.message },
      ])
    } finally {
      setExecutingCommand(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-red-400 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col overflow-hidden relative">
      {/* Header */}
      <div className="flex-none flex items-center gap-3 px-4 py-3 bg-gray-800 border-b border-gray-700">
        <Terminal className="w-5 h-5 text-green-400" />
        <span className="text-sm font-medium text-gray-200">
          {router?.name || t('routers.terminal.routerFallback')}
          {connectionStatus?.identity && ` (${connectionStatus.identity})`}
        </span>
        <span className={`ml-auto text-xs px-2 py-0.5 rounded ${connectionStatus?.connected ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
          {connectionStatus?.connected ? t('common.status.connected') : t('common.status.disconnected')}
        </span>
      </div>

      {/* Terminal output */}
      <div className={`flex-1 min-h-0 overflow-y-auto p-4 font-mono text-sm terminal-scroll ${ai.isOpen ? 'mr-[420px]' : ''}`}>
        {terminalHistory.length === 0 && (
          <div className="text-gray-500">
            {t('routers.terminal.introHint')}
          </div>
        )}
        {terminalHistory.map((entry, i) => (
          <div key={i} className="mb-2">
            {entry.type === 'command' && (
              <div className="text-green-400">
                <span className="text-gray-500">&gt; </span>
                {entry.source === 'ai' && (
                  <span className="text-[10px] font-semibold text-theme-primary bg-theme-primary/20 rounded px-1 py-0.5 mr-1 uppercase">AI</span>
                )}
                {entry.content}
              </div>
            )}
            {entry.type === 'result' && entry.content && (
              <pre className="text-gray-300 whitespace-pre-wrap">{entry.content}</pre>
            )}
            {entry.type === 'error' && (
              <div className="text-red-400">{entry.content}</div>
            )}
          </div>
        ))}
        <div ref={historyEndRef} />
      </div>

      {/* Input */}
      <div className={`flex-none flex items-center gap-2 px-4 py-3 bg-gray-800 border-t border-gray-700 ${ai.isOpen ? 'mr-[420px]' : ''}`}>
        <span className="text-green-400 font-mono text-sm">&gt;</span>
        <input
          type="text"
          value={terminalCommand}
          onChange={(e) => setTerminalCommand(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), executeTerminalCommand())}
          className="flex-1 bg-gray-700 text-gray-200 border border-gray-600 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-green-500"
          placeholder="/system/resource/print"
          disabled={!connectionStatus?.connected}
          autoFocus
        />
        <button
          type="button"
          onClick={executeTerminalCommand}
          disabled={executingCommand || !terminalCommand.trim() || !connectionStatus?.connected}
          className="px-3 py-2 bg-green-700 hover:bg-green-600 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {executingCommand ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {!ai.isOpen && (
        <TerminalAIButton
          onClick={ai.toggle}
          hasConversation={ai.messages.length > 0}
        />
      )}

      <TerminalAIPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        messages={ai.messages}
        loading={ai.loading}
        error={ai.error}
        settings={ai.settings}
        onSettingsChange={ai.setSettings}
        tokenStats={ai.tokenStats}
        onSend={ai.sendMessage}
        onRunCommand={ai.runCommand}
        onNewConversation={ai.newConversation}
        suModal={ai.suModal}
        confirmSu={ai.confirmSu}
        cancelSu={ai.cancelSu}
        isLinuxProfile={ai.isLinuxProfile}
        executionPlan={ai.executionPlan}
        approveExecutionPlan={ai.approveExecutionPlan}
        rejectExecutionPlan={ai.rejectExecutionPlan}
        onRetryAsLite={ai.retryAsLite}
      />
    </div>
  )
}
